import React, { useState, useEffect, useCallback } from "react";
import { Typography, Chip, Tooltip } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import { useAllProjectsViewModel } from "./AllProjectViewModel";
import AddIcon from "@mui/icons-material/Add";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import AddProject from "../AddProject/AddProject";
import BusinessIcon from "@mui/icons-material/Business";
import CircleIcon from "@mui/icons-material/Circle";
import WorkIcon from "@mui/icons-material/Work";
import CategoryIcon from "@mui/icons-material/Category";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningIcon from "@mui/icons-material/Warning";
import { getEnumLabel, getEnumOptions } from "../../../utils/enumUtils";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { useRbac } from "../../../hooks/RbacContext";
import { useAllProjectResorceMappingsViewModel } from "../../ProjectResourceMapping/ProjectResourceMappingAll/AllProjectResourceMappingViewModel";
import Link from "../../../components/Link/Link";
import { ROUTES } from "../../../constants";
import { useAllCountriesViewModel } from "../../ManageCountry/AllCountriesViewModel";

const AllProjects = () => {
  const {
    projects,
    loading,
    error,
    clients,
    clientDesignations,
    getClientName,
    createProject,
    updateProject,
    handleDelete,
    successModal,
    setSuccessModal,
    fetchClientPocs,
    clientPocs,
    resources = [],
    projectOwners = [],
  } = useAllProjectsViewModel();

  const { countries } = useAllCountriesViewModel();

  const { getResourceName } = useAllProjectResorceMappingsViewModel();

  const { showLoading, hideLoading } = useLoading();
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const { checkAccess } = useRbac();
  const columns = [
    {
      id: "project_name",
      label: "Project Name | Project Code",
      render: (value, row) => {
        const displayText = `${value} | ${row?.project_code || "-"}`;
        return (
          (
            <Tooltip title={displayText} arrow>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontSize: "inherit",
                  maxWidth: 200,
                  "& a": {
                    color: "inherit",
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                }}
              >
                <Link
                  to={ROUTES.PROJECT_DETAILS.replace(":id", row?.project_id)}
                >
                  {displayText}
                </Link>
              </Typography>
            </Tooltip>
          ) || "N/A"
        );
      },
      sortValue: (value, row) => `${value} | ${row?.project_code || "-"}`,
    },
    {
      id: "project_type",
      label: "Project Type",
      render: (value) => getEnumLabel("project_type", value),
      sortValue: (value) => getEnumLabel("project_type", value),
    },
    {
      id: "client_id",
      label: "Client Name",
      render: (value) => {
        const clientName = getClientName(value) || "N/A";
        return (
          <Tooltip title={clientName} arrow>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontSize: "inherit",
                maxWidth: 200,
                cursor: "pointer",
              }}
            >
              {clientName}
            </Typography>
          </Tooltip>
        );
      },
      searchValue: (value, row) => {
        const clientName = getClientName(row.client_id);
        return clientName || "";
      },
      sortValue: (value, row) => {
        const clientName = getClientName(row.client_id);
        return clientName || "";
      },
    },
    {
      id: "proposed_start_date",
      label: "Project Start Date",
      render: (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      id: "proposed_end_date",
      label: "Project End Date",
      render: (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      },
    },
    {
      id: "primary_project_manager",
      label: "Project Manager",
      render: (value) => {
        const resource = resources.find(
          (r) => r.resource_id.toString() === value?.toString()
        );
        return resource
          ? `${resource.first_name} ${resource.last_name} `
          : "N/A";
      },
      sortValue: (value) => {
        const resource = resources.find(
          (r) => r.resource_id.toString() === value?.toString()
        );
        return resource
          ? `${resource.first_name} ${resource.last_name}`
          : "N/A";
      },
    },
    {
      id: "project_owner",
      label: "Project Owner",
      render: (value) => {
        const owner = projectOwners.find(
          (r) => r.resource_id.toString() === value?.toString()
        );
        return owner ? `${owner.first_name} ${owner.last_name}` : "N/A";
      },
      sortValue: (value) => {
        const owner = projectOwners.find(
          (r) => r.resource_id.toString() === value?.toString()
        );
        return owner ? `${owner.first_name} ${owner.last_name}` : "N/A";
      },
    },
    // {
    //   id: "purchase_order_number",
    //   label: "PO Number",
    //   render: (value) => value || "N/A",
    // },
    // {
    //   id: "project_description",
    //   label: "Description",
    //   render: (value) => value || "N/A",
    // },
    // {
    //   id: "proposed_start_date",
    //   label: "Start Date",
    // },
    // {
    //   id: "proposed_end_date",
    //   label: "End Date",
    // },
    // {
    //   id: "revised_start_date",
    //   label: "Revised Start",
    // },
    // {
    //   id: "revised_end_date",
    //   label: "Revised End",
    // },
    // {
    //   id: "billability",
    //   label: "Billability",
    //   render: (value) => value || "N/A",
    // },
    // {
    //   id: "project_priority_code",
    //   label: "Priority",
    //   render: (value) => (
    //     <Chip
    //       label={getEnumLabel("project_priority_code", value)}
    //       size="small"
    //       sx={{
    //         height: 24,
    //         fontSize: "0.75rem",
    //         fontWeight: 500,
    //         backgroundColor:
    //           value === "HIGH"
    //             ? "#FFEBEE"
    //             : value === "MEDIUM"
    //             ? "#FFF3E0"
    //             : value === "LOW"
    //             ? "#E8F5E9"
    //             : "#E3F2FD",
    //         color:
    //           value === "HIGH"
    //             ? "#C62828"
    //             : value === "MEDIUM"
    //             ? "#EF6C00"
    //             : value === "LOW"
    //             ? "#2E7D32"
    //             : "#1565C0",
    //         "& .MuiChip-label": { px: 1.5 },
    //       }}
    //     />
    //   ),
    // },
    {
      id: "project_status_code",
      label: "Status",
      sortValue: (value) => {
        const statusLabel = getEnumLabel("project_status_code", value);
        return statusLabel || "";
      },
      render: (value) => (
        <Chip
          label={getEnumLabel("project_status_code", value)}
          size="small"
          sx={{
            height: 24,
            fontSize: "0.75rem",
            fontWeight: 500,
            backgroundColor:
              value === "IN_PROGRESS"
                ? "#E8F5E9"
                : value === "ON_HOLD"
                ? "#FFF3E0"
                : value === "COMPLETED"
                ? "#E3F2FD"
                : value === "PENDING"
                ? "#FFF8E1"
                : value === "APPROVED"
                ? "#E8F5E9"
                : "#FFEBEE",
            color:
              value === "IN_PROGRESS"
                ? "#2E7D32"
                : value === "ON_HOLD"
                ? "#EF6C00"
                : value === "COMPLETED"
                ? "#1565C0"
                : value === "PENDING"
                ? "#F57F17"
                : value === "APPROVED"
                ? "#2E7D32"
                : "#C62828",
            "& .MuiChip-label": { px: 1.5 },
          }}
        />
      ),
    },
    // {
    //   id: "risk",
    //   label: "Risk",
    //   render: (value) => (
    //     <Chip
    //       label={getEnumLabel("risk", value)}
    //       size="small"
    //       sx={{
    //         height: 24,
    //         fontSize: "0.75rem",
    //         fontWeight: 500,
    //         backgroundColor:
    //           value === "BUDGET_OVERRUN"
    //             ? "#FFEBEE"
    //             : value === "TECH_CHALLENGE"
    //             ? "#FFF3E0"
    //             : value === "RESOURCE_CONSTRAINT"
    //             ? "#E8F5E9"
    //             : "#E3F2FD",
    //         color:
    //           value === "BUDGET_OVERRUN"
    //             ? "#C62828"
    //             : value === "TECH_CHALLENGE"
    //             ? "#EF6C00"
    //             : value === "RESOURCE_CONSTRAINT"
    //             ? "#2E7D32"
    //             : "#1565C0",
    //         "& .MuiChip-label": { px: 1.5 },
    //       }}
    //     />
    //   ),
    // },
    // {
    //   id: "communication",
    //   label: "Communication",
    //   render: (value) => getEnumLabel("communication", value),
    // },
    // {
    //   id: "budget_hours",
    //   label: "Budget Hours",
    //   render: (value) => value || "N/A",
    // },
    // {
    //   id: "currency_code",
    //   label: "Currency",
    //   render: (value) => getEnumLabel("currency_code", value),
    // },
    // {
    //   id: "created_on",
    //   label: "Created On",
    // },
    // {
    //   id: "updated_on",
    //   label: "Updated On",
    // },
  ];

  const fixedColumns = {
    left: [],
    right: ["project_status_code", "actions"],
  };

  const columnFilters = [
    // {
    //   id: "project_name",
    //   type: "select",
    //   label: "Project",
    //   icon: <WorkIcon />,
    //   sendIcon: true,
    // },

    {
      id: "client_id",
      type: "select",
      label: "Client name",
      render: (value) => getClientName(value),
      icon: <BusinessIcon />,
      sendIcon: true,
    },
    {
      id: "project_type",
      type: "select",
      label: "Project Type",
      options: getEnumOptions("project_type"),
      render: (value) => getEnumLabel("project_type", value),
      icon: <CategoryIcon />,
      sendIcon: true,
    },
    {
      id: "project_status_code",
      type: "select",
      label: "Status",
      options: getEnumOptions("project_status_code"),
      render: (value) => getEnumLabel("project_status_code", value),
      icon: <CircleIcon />,
      sendIcon: true,
    },
    {
      id: "primary_project_manager",
      type: "select",
      label: "PM",
      icon: <WorkIcon />,
      sendIcon: true,
      render: (value) => {
        const resource = resources.find(
          (r) => r.resource_id.toString() === value?.toString()
        );
        return resource
          ? `${resource.first_name} ${resource.last_name}`
          : "N/A";
      },
    },
    {
      id: "project_owner",
      type: "select",
      label: "Project Owner",
      icon: <WorkIcon />,
      sendIcon: true,
      render: (value) => {
        const owner = projectOwners.find(
          (r) => r.resource_id.toString() === value?.toString()
        );
        return owner ? `${owner.first_name} ${owner.last_name}` : "N/A";
      },
    },
    // {
    //   id: "project_priority_code",
    //   type: "select",
    //   label: "Priority",
    //   options: getEnumOptions("project_priority_code"),
    //   icon: <PriorityHighIcon />,
    //   sendIcon: true,
    // },
    // {
    //   id: "risk",
    //   type: "select",
    //   label: "Risk",
    //   options: getEnumOptions("risk"),
    //   icon: <WarningIcon />,
    //   sendIcon: true,
    // },
  ];

  const handleAddProject = () => {
    setSelectedProject(null);
    setIsAddProjectOpen(true);
  };

  const handleEditProject = (project) => {
    // Ensure project_pocs_mapping exists and is an array
    setSelectedProject(project);
    setIsAddProjectOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddProjectOpen(false);
    setSelectedProject(null);
  };

  const handleSubmit = useCallback(
    async (formData) => {
      // Validate project_budget_hours > 0
      console.log("formData>>>", formData);
      if (!formData.budget_hours || Number(formData.budget_hours) <= 0) {
       
        return { success: false, error: "Project budget hours must be greater than 0." };
      }
      try {
        showLoading();
        console.log("formData>>>", formData);

        const processedFormData = {
          ...formData,
          project_pocs_mapping: [
            {
              client_poc_id: parseInt(formData.project_poc_id),
              is_sponsor: false,
            },
            {
              client_poc_id: parseInt(formData.sponsor_id),
              is_sponsor: true,
            },
          ],
        };
        let result;
        if (selectedProject) {
          result = await updateProject(
            selectedProject.project_id?.toString(),
            processedFormData
          );
        } else {
          result = await createProject(processedFormData);
        }

        if (!result.success) {
          setSuccessModal({
            type: "error",
            title: "Action Failed",
            message: result.error,
          });
          return { success: false, error: result.error };
        }

        setSuccessModal({
          type: "success",
          title: "Success",
          message: selectedProject
            ? "Project updated successfully"
            : "Project added successfully",
        });
        handleCloseDialog();
        return { success: true };
      } catch (error) {
        // setSuccessModal({
        //   type: "error",
        //   title: "Action Failed",
        //   message: error.message || "An error occurred",
        // });
        return { success: false, error: error.message };
      } finally {
        hideLoading();
      }
    },
    [
      selectedProject,
      updateProject,
      createProject,
      showLoading,
      hideLoading,
      handleCloseDialog,
    ]
  );

  const handleDeleteClick = (project) => {
    setDeleteConfirmation({
      project,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const project = deleteConfirmation.project;
    handleDelete(project);
    setDeleteConfirmation(null);
  };

  const getActions = (project) => {
    const actions = [];

    if (checkAccess("projects", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditProject,
        color: "primary",
        disabled: project?.is_default,
        disable: project?.is_default,
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("projects", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: () => setDeleteConfirmation({ project }),
        color: "error",
        disabled: project?.is_default,
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(244, 67, 54, 0.08)",
          },
        },
      });
    }
    return actions;
  };

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }
  console.log("data13", successModal);
  return (
    <div>
      <CustomTable
        columns={columns}
        data={projects}
        loading={loading}
        onAdd={handleAddProject}
        showActions={true}
        showAddButton={checkAccess("projects", "add")}
        addButtonIcon="/assets/project_add_icon.png"
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Projects"
        subtitle="Manage and monitor all projects"
        addButtonText="New Project"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddProject
        open={isAddProjectOpen}
        onClose={handleCloseDialog}
        initialData={selectedProject}
        onSubmit={handleSubmit}
        clients={clients}
        resources={resources}
        projectOwners={projectOwners}
        clientDesignations={clientDesignations}
        fetchClientPocs={fetchClientPocs}
        clientPocs={clientPocs}
        countries={countries}
        onDependencyChange={(field, value, setFieldValue) => {
          if (field === "client_id" && value) {
            fetchClientPocs(value);
          } else if (field === "project_poc_id" && value) {
            const selectedPoc = clientPocs.find(
              (poc) => parseInt(poc.client_poc_id) === parseInt(value)
            );
            if (selectedPoc) {
              setFieldValue(
                "project_poc_business_unit",
                selectedPoc.poc_business_unit || ""
              );
              setFieldValue(
                "project_poc_countries",
                selectedPoc.poc_country_name || ""
              );
            }
          }
        }}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={() => setSuccessModal(null)}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Project"
          message={`Are you sure you want to delete project "${deleteConfirmation.project.project_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default AllProjects;
